import { useId, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { AccessDenied } from '@/components/RouteError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { errorMessage } from '@/lib/errors';
import {
  financeAccountsQuery,
  financeCategoriesQuery,
  financeCounterpartiesQuery,
  financeEnvelopesQuery,
  financeFxRatesQuery,
  financeSettingsQuery,
  newOperationId,
  todayInput,
  useCreateFinanceCategory,
  useCreateFinanceCounterparty,
  useRecomputeFinanceFx,
  useSetFinanceFxRate,
  useUpdateFinanceCategory,
  useUpdateFinanceCounterparty,
  useUpdateFinanceSettings,
} from '@/lib/finance';
import { usePermissions } from '@/lib/me';
import { CURRENCIES } from '@/lib/money';
import type {
  FinanceCategory,
  FinanceCategoryInput,
  FinanceCounterparty,
  FinanceCounterpartyInput,
  FinanceSettings,
} from '@/api/generated/types.gen';

export const Route = createFileRoute('/_app/settings/finance')({
  component: FinanceSettingsPage,
});

// Date-only arithmetic in UTC: a calendar day has no zone here, and using
// local time would shift the window across a DST boundary.
function minusDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) - n)).toISOString().slice(0, 10);
}

function FinanceSettingsPage() {
  const { can } = usePermissions();
  const [tab, setTab] = useState('categories');
  if (!can('finance.manage')) return <AccessDenied />;
  return (
    <div className="flex flex-col gap-5">
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'categories', label: 'Categories' },
          { value: 'counterparties', label: 'Counterparties' },
          { value: 'channels', label: 'Channel defaults' },
          { value: 'fx', label: 'FX rates' },
        ]}
      />
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'counterparties' && <CounterpartiesTab />}
      {tab === 'channels' && <ChannelDefaultsTab />}
      {tab === 'fx' && <FxTab />}
    </div>
  );
}

// --- Categories -------------------------------------------------------------

function CategoriesTab() {
  const list = useQuery(financeCategoriesQuery(true));
  const [editing, setEditing] = useState<FinanceCategory | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          New category
        </Button>
      </div>
      {list.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(list.error)}
        </p>
      )}
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
              <th className="px-4 py-2.5 font-semibold">Name</th>
              <th className="px-4 py-2.5 font-semibold">Code</th>
              <th className="px-4 py-2.5 font-semibold">Kind</th>
              <th className="px-4 py-2.5 font-semibold">In P&amp;L</th>
              <th className="px-4 py-2.5 font-semibold">Flags</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((c) => (
              <tr key={c.id} className="border-t border-[var(--border-hairline)]">
                <td className="px-4 py-3">{c.name}</td>
                <td className="px-4 py-3"><code className="text-xs">{c.code}</code></td>
                <td className="px-4 py-3">{c.kind}</td>
                <td className="px-4 py-3">{c.in_pnl ? 'yes' : 'no'}</td>
                <td className="px-4 py-3">
                  <span className="flex flex-wrap gap-1">
                    {c.is_system && <Badge variant="navy">System</Badge>}
                    {!c.is_active && <Badge variant="neutral">Inactive</Badge>}
                    {c.requires_counterparty && <Badge variant="gold">Needs counterparty</Badge>}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(creating || editing) && (
        <CategoryDialog category={editing} onClose={() => { setCreating(false); setEditing(null); }} />
      )}
    </section>
  );
}

export function CategoryDialog({
  category,
  onClose,
}: {
  category: FinanceCategory | null;
  onClose: () => void;
}) {
  const accounts = useQuery(financeAccountsQuery(true));
  // A switched-off envelope is not offered as a hint (spec §7); only the one
  // this category ALREADY holds stays visible (marked off), so the select
  // never shows «None» while a save would re-send the old id.
  const envelopes = useQuery(financeEnvelopesQuery({ includeInactive: true }));
  const [name, setName] = useState(category?.name ?? '');
  const [code, setCode] = useState(category?.code ?? '');
  const [kind, setKind] = useState<FinanceCategoryInput['kind']>(category?.kind ?? 'expense');
  const [expenseType, setExpenseType] = useState(category?.expense_type_default ?? '');
  const [inPnl, setInPnl] = useState(category?.in_pnl ?? true);
  const [defaultAccountId, setDefaultAccountId] = useState(category?.default_account_id ?? '');
  const [defaultEnvelopeId, setDefaultEnvelopeId] = useState(category?.default_envelope_id ?? '');
  const envelopeOptions = (envelopes.data?.envelopes ?? [])
    .filter((e) => e.is_active || e.id === category?.default_envelope_id)
    .map((e) => ({ value: e.id, label: e.is_active ? e.name : `${e.name} (switched off)` }));
  // The held id the list does not name: still loading, the list failed, or a
  // loaded list that does not hold it.
  const heldEnvelopeLabel = envelopes.isPending
    ? 'Loading…'
    : envelopes.isError
      ? "(couldn't load the envelopes)"
      : 'Unknown envelope';
  const [defaultVat, setDefaultVat] = useState(category?.default_vat ?? false);
  const [requiresCp, setRequiresCp] = useState(category?.requires_counterparty ?? false);
  const [isActive, setIsActive] = useState(category?.is_active ?? true);
  const [sort, setSort] = useState(String(category?.sort ?? 0));
  const [failure, setFailure] = useState<string | null>(null);
  const create = useCreateFinanceCategory();
  const update = useUpdateFinanceCategory();
  const pending = create.isPending || update.isPending;

  // A system category's kind and in_pnl are locked server-side (409), and
  // `code` is immutable for every category — so those inputs are read-only
  // rather than a click that turns into a conflict.
  const system = category?.is_system ?? false;
  // A category outside P&L whose default type is financing is a financing
  // category: its lines count only when typed financing, and that default is
  // what marks it — so the server refuses another default, «None» included,
  // unless the same save moves the category into P&L. The rule is on the
  // saved category and the resulting in_pnl, and the type shown is the type
  // sent: one picked while in P&L does not survive unticking it again.
  const financingOnly = category !== null && !category.in_pnl && category.expense_type_default === 'financing';
  const expenseTypeHeld = financingOnly && !inPnl;
  const shownExpenseType = expenseTypeHeld ? 'financing' : expenseType;
  const expenseTypeHintId = useId();
  const ready = name.trim().length > 0 && /^[a-z][a-z0-9_]{1,40}$/.test(code);

  function submit() {
    setFailure(null);
    const body: FinanceCategoryInput = {
      // PATCH takes the whole category, not a delta, and the type requires
      // `code` — the unchanged one is sent back deliberately.
      code,
      name: name.trim(),
      kind,
      in_pnl: inPnl,
      default_vat: defaultVat,
      requires_counterparty: requiresCp,
      is_active: isActive,
      sort: Number.parseInt(sort, 10) || 0,
    };
    if (shownExpenseType) {
      body.expense_type_default = shownExpenseType as NonNullable<FinanceCategoryInput['expense_type_default']>;
    }
    if (defaultAccountId) body.default_account_id = defaultAccountId;
    // Always sent — a value or an explicit null (clear): omitting it would keep
    // the old one. The generated type drops the null arm, so the body is
    // laundered through a cast (see rawBody elsewhere).
    (body as Record<string, unknown>)['default_envelope_id'] = defaultEnvelopeId || null;
    const opts = { onSuccess: onClose, onError: (e: unknown) => setFailure(errorMessage(e)) };
    if (category) update.mutate({ id: category.id, body }, opts);
    else create.mutate(body, opts);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={category ? `Edit ${category.name}` : 'New category'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!ready} loading={pending}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={!!category}
          hint={category ? 'Immutable' : 'lower_snake_case, 2–41 characters'}
          required
        />
        <Select
          label="Kind"
          value={kind}
          disabled={system}
          options={[
            { value: 'income', label: 'Income' },
            { value: 'expense', label: 'Expense' },
            { value: 'transfer', label: 'Transfer' },
            { value: 'investment', label: 'Investment' },
            { value: 'correction', label: 'Correction' },
          ]}
          onChange={(e) => setKind(e.target.value as FinanceCategoryInput['kind'])}
        />
        <div className="flex flex-col gap-1">
          <Select
            label="Default expense type"
            value={shownExpenseType}
            disabled={expenseTypeHeld}
            aria-describedby={expenseTypeHeld ? expenseTypeHintId : undefined}
            options={[
              { value: '', label: 'None' },
              { value: 'operational', label: 'Operational' },
              { value: 'capital', label: 'Capital' },
              { value: 'financing', label: 'Financing' },
            ]}
            onChange={(e) => setExpenseType(e.target.value)}
          />
          {expenseTypeHeld && (
            <p id={expenseTypeHintId} className="text-xs text-muted">
              A financing category outside P&amp;L keeps the Financing type.
            </p>
          )}
        </div>
        <Select
          label="Default account"
          value={defaultAccountId}
          options={[
            { value: '', label: 'None' },
            ...(accounts.data ?? []).map((a) => ({ value: a.id, label: a.name })),
          ]}
          onChange={(e) => setDefaultAccountId(e.target.value)}
        />
        <div className="flex flex-col gap-1">
          <Select
            label="Default envelope"
            value={defaultEnvelopeId}
            options={[
              { value: '', label: 'None' },
              ...envelopeOptions,
              // The held id before the list knows it (still loading, or the
              // list failed): shown as itself, never as «None», because Save
              // sends it back either way.
              ...(defaultEnvelopeId && !envelopeOptions.some((o) => o.value === defaultEnvelopeId)
                ? [{ value: defaultEnvelopeId, label: heldEnvelopeLabel }]
                : []),
            ]}
            onChange={(e) => setDefaultEnvelopeId(e.target.value)}
          />
          {envelopes.isError && (
            <p role="alert" className="text-xs text-destructive">
              Couldn&apos;t load the envelopes: {errorMessage(envelopes.error)}
            </p>
          )}
          <p className="text-xs text-muted">
            Only a hint: the payment form suggests this envelope for a line of this category.
          </p>
        </div>
        <Input label="Sort" type="number" value={sort} onChange={(e) => setSort(e.target.value)} />
        <Checkbox
          label="Counts in P&L"
          checked={inPnl}
          disabled={system}
          onChange={(e) => setInPnl(e.target.checked)}
        />
        <Checkbox label="VAT by default" checked={defaultVat} onChange={(e) => setDefaultVat(e.target.checked)} />
        <Checkbox
          label="Requires a counterparty"
          checked={requiresCp}
          onChange={(e) => setRequiresCp(e.target.checked)}
        />
        <Checkbox label="Active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        {system && (
          <p className="text-xs text-muted">
            A system category: the engine resolves it by code, so its kind and P&amp;L flag are
            fixed and deactivating it is refused while the code is in use.
          </p>
        )}
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// --- Counterparties ---------------------------------------------------------

function CounterpartiesTab() {
  const list = useQuery(financeCounterpartiesQuery(true));
  const [editing, setEditing] = useState<FinanceCounterparty | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <section className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          New counterparty
        </Button>
      </div>
      {list.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(list.error)}
        </p>
      )}
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
              <th className="px-4 py-2.5 font-semibold">Name</th>
              <th className="px-4 py-2.5 font-semibold">Kind</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((c) => (
              <tr key={c.id} className="border-t border-[var(--border-hairline)]">
                <td className="px-4 py-3">{c.name}</td>
                <td className="px-4 py-3">{c.kind ?? '—'}</td>
                <td className="px-4 py-3">
                  {c.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="neutral">Inactive</Badge>}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(creating || editing) && (
        <CounterpartyDialog
          counterparty={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

function CounterpartyDialog({
  counterparty,
  onClose,
}: {
  counterparty: FinanceCounterparty | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(counterparty?.name ?? '');
  const [kind, setKind] = useState(counterparty?.kind ?? '');
  const [isActive, setIsActive] = useState(counterparty?.is_active ?? true);
  const [notes, setNotes] = useState(counterparty?.notes ?? '');
  const [failure, setFailure] = useState<string | null>(null);
  const create = useCreateFinanceCounterparty();
  const update = useUpdateFinanceCounterparty();
  const pending = create.isPending || update.isPending;

  function submit() {
    setFailure(null);
    const body: FinanceCounterpartyInput = { name: name.trim(), is_active: isActive };
    if (kind) body.kind = kind as NonNullable<FinanceCounterpartyInput['kind']>;
    if (notes.trim()) body.notes = notes.trim();
    const opts = { onSuccess: onClose, onError: (e: unknown) => setFailure(errorMessage(e)) };
    if (counterparty) update.mutate({ id: counterparty.id, body }, opts);
    else create.mutate(body, opts);
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={counterparty ? `Edit ${counterparty.name}` : 'New counterparty'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!name.trim()} loading={pending}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Select
          label="Kind"
          value={kind}
          options={[
            { value: '', label: 'None' },
            { value: 'vendor', label: 'Vendor' },
            { value: 'employee', label: 'Employee' },
            { value: 'investor', label: 'Investor' },
            { value: 'landlord', label: 'Landlord' },
            { value: 'platform', label: 'Platform' },
            { value: 'other', label: 'Other' },
          ]}
          onChange={(e) => setKind(e.target.value)}
        />
        <Input label="Notes" multiline value={notes} onChange={(e) => setNotes(e.target.value)} />
        <Checkbox label="Active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// --- Channel defaults -------------------------------------------------------

const CHANNELS = [
  { key: 'airbnb', label: 'Airbnb' },
  { key: 'booking_com', label: 'Booking.com' },
  { key: 'direct', label: 'Direct' },
] as const;

function ChannelDefaultsTab() {
  const settings = useQuery(financeSettingsQuery);
  const accounts = useQuery(financeAccountsQuery(true));
  if (settings.isLoading) return <p className="text-sm text-muted">Loading settings…</p>;
  if (settings.isError || !settings.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {errorMessage(settings.error)}
      </p>
    );
  }
  return (
    <ChannelDefaultsForm key={JSON.stringify(settings.data)} settings={settings.data} accounts={accounts.data ?? []} />
  );
}

export function ChannelDefaultsForm({
  settings,
  accounts,
}: {
  settings: FinanceSettings;
  accounts: readonly { id: string; name: string; currencies: string[] }[];
}) {
  // The whole map is held in state, seeded from the server's answer, because
  // the PATCH REPLACES channel_defaults wholesale when the field is present
  // (api/v1/finance_refs.go: `next.ChannelDefaults = make(...)`). The server
  // merges at field level, not at map-key level, so sending one channel would
  // erase the other two with a 200 on the way out.
  const [map, setMap] = useState<Record<string, { account_id: string; currency: string }>>(() =>
    Object.fromEntries(
      Object.entries(settings.channel_defaults).map(([k, v]) => [
        k,
        { account_id: v.account_id, currency: v.currency },
      ]),
    ),
  );
  const [fxSource, setFxSource] = useState(settings.fx_source);
  const [tz, setTz] = useState(settings.business_tz);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const mut = useUpdateFinanceSettings();

  return (
    <section className="flex flex-col gap-4">
      {CHANNELS.map((ch) => {
        const cur = map[ch.key];
        const acc = accounts.find((a) => a.id === cur?.account_id);
        return (
          <div key={ch.key} className="grid gap-3 sm:grid-cols-3 sm:items-end">
            <Select
              label={`${ch.label} account`}
              value={cur?.account_id ?? ''}
              // Not a `placeholder`: unrouting a channel has to be reachable,
              // and Select's placeholder option is disabled (ui/select.tsx).
              options={[
                { value: '', label: 'Not routed' },
                ...accounts.map((a) => ({ value: a.id, label: a.name })),
              ]}
              onChange={(e) => {
                setSaved(false);
                const id = e.target.value;
                setMap((prev) => {
                  // Rebuilt without the key rather than deleted out of a
                  // copy: an unrouted channel must be ABSENT from the map,
                  // not present with an empty account_id — the server checks
                  // every entry's account exists (422).
                  const rest = Object.fromEntries(
                    Object.entries(prev).filter(([k]) => k !== ch.key),
                  );
                  if (!id) return rest;
                  const first = accounts.find((a) => a.id === id)?.currencies[0] ?? 'EUR';
                  return { ...rest, [ch.key]: { account_id: id, currency: first } };
                });
              }}
            />
            <Select
              label="Currency"
              value={cur?.currency ?? ''}
              // Only the currencies the chosen account holds: the server does
              // NOT check this pairing (validateSettings only checks that the
              // currency exists), so a mismatch would sit in the settings and
              // fail later, at the first payment routed through it.
              options={(acc?.currencies ?? []).map((c) => ({ value: c, label: c }))}
              disabled={!cur}
              onChange={(e) => {
                setSaved(false);
                setMap((prev) => {
                  const at = prev[ch.key];
                  if (!at) return prev;
                  return { ...prev, [ch.key]: { ...at, currency: e.target.value } };
                });
              }}
            />
          </div>
        );
      })}

      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="FX source"
          value={fxSource}
          options={[
            { value: 'nbs', label: 'NBS (fetched daily)' },
            { value: 'manual', label: 'Manual only' },
          ]}
          onChange={(e) => {
            setSaved(false);
            setFxSource(e.target.value === 'manual' ? 'manual' : 'nbs');
          }}
        />
        <Input
          label="Business timezone"
          value={tz}
          onChange={(e) => {
            setSaved(false);
            setTz(e.target.value);
          }}
          hint="An IANA zone, e.g. Europe/Belgrade"
        />
      </div>

      <p className="text-xs text-muted">
        Import cutoff {settings.import_cutoff} · income side{' '}
        {settings.income_side_enabled ? 'on' : 'off'} — both read-only here.
      </p>

      {failure && (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      )}
      {saved && <p className="text-sm text-[var(--ollio-success)]">Saved.</p>}

      <div className="flex justify-end">
        <Button
          variant="primary"
          loading={mut.isPending}
          onClick={() => {
            setFailure(null);
            mut.mutate(
              { channel_defaults: map, fx_source: fxSource, business_tz: tz.trim() },
              { onSuccess: () => setSaved(true), onError: (e) => setFailure(errorMessage(e)) },
            );
          }}
        >
          Save settings
        </Button>
      </div>
    </section>
  );
}

// --- FX ---------------------------------------------------------------------

function FxTab() {
  const settings = useQuery(financeSettingsQuery);
  const today = todayInput(settings.data?.business_tz);
  const from = minusDays(today, 29);
  const rates = useQuery(financeFxRatesQuery(from, today));
  const [manual, setManual] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={() => setRecomputing(true)}>
          Recompute open months
        </Button>
        <Button variant="primary" size="sm" onClick={() => setManual(true)}>
          Set a manual rate
        </Button>
      </div>
      <p className="text-sm text-muted">
        {from} → {today}. A rate is units of currency per 1 EUR; a manual row wins over the NBS row
        for the same date.
      </p>
      {rates.isError && (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(rates.error)}
        </p>
      )}
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border-hairline)] text-left text-xs uppercase tracking-[0.06em] text-muted">
              <th className="px-4 py-2.5 font-semibold">Date</th>
              <th className="px-4 py-2.5 font-semibold">Currency</th>
              <th className="px-4 py-2.5 font-semibold">Per 1 EUR</th>
              <th className="px-4 py-2.5 font-semibold">Source</th>
            </tr>
          </thead>
          <tbody>
            {(rates.data ?? []).map((r) => (
              <tr key={`${r.rate_date}-${r.currency}`} className="border-t border-[var(--border-hairline)]">
                <td className="px-4 py-3">{r.rate_date}</td>
                <td className="px-4 py-3">{r.currency}</td>
                <td className="px-4 py-3 tabular-nums">{(r.rate_micro / 1_000_000).toFixed(6)}</td>
                <td className="px-4 py-3">
                  <Badge variant={r.source === 'manual' ? 'gold' : 'neutral'}>{r.source}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(rates.data ?? []).length === 0 && !rates.isLoading && (
        <p className="text-sm text-muted">No rates in this window.</p>
      )}
      {manual && <ManualRateDialog defaultDate={today} onClose={() => setManual(false)} />}
      {recomputing && (
        <RecomputeDialog from={from} to={today} onClose={() => setRecomputing(false)} />
      )}
    </section>
  );
}

function ManualRateDialog({ defaultDate, onClose }: { defaultDate: string; onClose: () => void }) {
  const [date, setDate] = useState(defaultDate);
  // EUR is refused by the server: §6.2 converts EUR by the identity rate
  // without reading the table at all, so it is not offered here.
  const options = CURRENCIES.filter((c) => c !== 'EUR');
  const [currency, setCurrency] = useState(options.includes('RSD') ? 'RSD' : (options[0] ?? ''));
  const [rate, setRate] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const mut = useSetFinanceFxRate();

  // rate_micro is the rate × 10^6 and must be a positive integer; a rate
  // typed with more than six decimals would otherwise be rounded silently.
  const parsed = /^\d+(\.\d{1,6})?$/.test(rate.trim()) ? Number(rate.trim()) : NaN;
  const micro = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 1_000_000) : null;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Manual FX rate"
      description="Units of the currency per 1 EUR, e.g. 117.20 for RSD."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={micro === null || !date || !currency}
            loading={mut.isPending}
            onClick={() => {
              if (micro === null) return;
              setFailure(null);
              mut.mutate(
                { date, currency, rateMicro: micro },
                { onSuccess: onClose, onError: (e) => setFailure(errorMessage(e)) },
              );
            }}
          >
            Save rate
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Select
          label="Currency"
          value={currency}
          options={options.map((c) => ({ value: c, label: c }))}
          onChange={(e) => setCurrency(e.target.value)}
        />
        <Input
          label="Rate per 1 EUR"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          error={rate.trim() && micro === null ? 'A positive number, at most six decimals.' : undefined}
          required
        />
        {failure && (
          <p role="alert" className="text-sm text-destructive">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function RecomputeDialog({
  from,
  to,
  onClose,
}: {
  from: string;
  to: string;
  onClose: () => void;
}) {
  const [f, setF] = useState(from);
  const [t, setT] = useState(to);
  const [operationId] = useState(newOperationId);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<{ movements_updated: number; rounding_lines_created: number; rounding_lines_deleted: number } | null>(null);
  const mut = useRecomputeFinanceFx();
  const ready = !!f && !!t && f <= t;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Recompute EUR equivalents"
      description="Re-resolves the rate for payments stamped fx_source=nbs in OPEN months. Closed months are skipped, not refused."
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!ready}
              loading={mut.isPending}
              onClick={() => {
                setFailure(null);
                mut.mutate(
                  { operation_id: operationId, from: f, to: t },
                  { onSuccess: setDone, onError: (e) => setFailure(errorMessage(e)) },
                );
              }}
            >
              Recompute
            </Button>
          </>
        )
      }
    >
      {done ? (
        <ul className="flex flex-col gap-1 text-sm">
          <li>{done.movements_updated} payments updated</li>
          <li>{done.rounding_lines_created} rounding lines created</li>
          <li>{done.rounding_lines_deleted} rounding lines deleted</li>
        </ul>
      ) : (
        <div className="flex flex-col gap-4">
          <Input label="From" type="date" value={f} onChange={(e) => setF(e.target.value)} />
          <Input label="To" type="date" value={t} onChange={(e) => setT(e.target.value)} />
          {!ready && <p className="text-xs text-[var(--ollio-gold-deep)]">From must not be after To.</p>}
          {failure && (
            <p role="alert" className="text-sm text-destructive">
              {failure}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
